var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from '@mui/material/Box';
import { InputFieldType } from 'models/Input/InputModels';
import { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { JsonViewer } from 'components/Shared/JsonViewer/JsonViewer';
import { useLanguageCode } from 'services/helperFunctions/useLanguageCode';
import Typography from '@mui/material/Typography';
import { InputFieldsContainer } from 'components/Shared/InputFieldsContainer/InputFieldsContainer';
import { StyledDialogV2 } from 'components/Shared/StyledDialogV2/StyledDialogV2';
import { LoadingSpinner } from 'components/Shared/Loading/LoadingSpinner/LoadingSpinner';
import { ChildKitsModal } from './ChildKitsModal/ChildKitsModal';
import { BuildBox } from '../BuildBox';
import styles from './BuildProductDetails.module.scss';
var PRODUCT_PREFIX = 'productPrefix';
var CHANNELS = 'channels';
var MIN_LEAD_TIME = 'minLeadtime';
var KIT_PARENT = 'kitParent';
var ORDERABLEIND = 'orderableInd';
var PRICE_MATRIX_ID = 'priceMatrixId';
var PRODUCT_VERSION = 'productVersion';
var PRODUCT_START_DATE = 'productStartDate';
var PRODUCT_END_DATE = 'productEndDate';
var BASE_PRODUCT_DESCRIPTION = 'baseProductDescription';
var BUSINESS_UNIT_CODE = 'businessUnitCode';
var BRAND_CODE = 'brandCode';
var PRODUCT_CLASS = 'productClass';
var DISCOUNT_CODE = 'discountCode';
var PRODUCT_LINE_CODE = 'productLineCode';
var PRODUCT_FUNCTION_CODE = 'productFunctionCode';
var WEIGHT_CLASS_CODE = 'weightClassCode';
var STANDARD_PRODUCT_REFERENCE = 'standardProductReference';
var COMPLEXITY = 'complexity';
var PROPRIETARY_IND = 'proprietaryInd';
var FSC_TYPE = 'fscType';
var FSC_CHAIN_OF_CUSTODY = 'fscChainOfCustody';
var REQUESTOR = 'requestor';
var QUOTE_STATUS = 'quoteStatus';
var ENGINEERING_STATUS = 'engineeringStatus';
var ENGINEERING_SITE = 'engineeringSite';
var DEVIATION_DESCRIPTION = 'deviationDescription';
export function BuildProductDetails(_a) {
    var _b, _c, _d;
    var _e, _f, _g, _h, _j, _k;
    var latestValidateResponse = _a.latestValidateResponse, infoResponse = _a.infoResponse, routeItems = _a.routeItems, timestamp = _a.timestamp, channelVal = _a.channelVal, buildMode = _a.buildMode, showCustom = _a.showCustom, listOfKitParents = _a.listOfKitParents;
    var _l = useState(true), isLoading = _l[0], setIsLoading = _l[1];
    var languageCode = useLanguageCode();
    var channelsData = (_g = (_f = (_e = infoResponse.details) === null || _e === void 0 ? void 0 : _e.header) === null || _f === void 0 ? void 0 : _f.channels) !== null && _g !== void 0 ? _g : {};
    var channelValue = Object.keys(channelsData).join(', ').replace('NA Contract', 'Commercial');
    var listOfParentsForKit = useMemo(function () {
        if (listOfKitParents)
            return listOfKitParents;
        return [];
    }, [listOfKitParents]);
    var fieldValues = useMemo(function () {
        var fieldVals = buildFieldValues(latestValidateResponse, infoResponse, channelValue, listOfParentsForKit);
        if (fieldVals) {
            setIsLoading(false);
        }
        return fieldVals;
    }, [latestValidateResponse, infoResponse, channelValue, listOfParentsForKit]);
    var _m = useState(false), isModalOpen = _m[0], setIsModalOpen = _m[1];
    var _o = useState(''), modalParam = _o[0], setModalParam = _o[1];
    var isCompleteProduct = useMemo(function () { return latestValidateResponse.completeProductInd; }, [latestValidateResponse]);
    var _p = useState(false), isJsonViewerOpen = _p[0], setJsonViewerOpen = _p[1];
    var fieldValuesCode = useState(buildFieldValuesCodes(latestValidateResponse))[0];
    var feildValuesCustom = useState(buildFieldValuesCustom(infoResponse))[0];
    var catalogGroups = [];
    var hierarchyList = (_k = (_j = (_h = infoResponse.details) === null || _h === void 0 ? void 0 : _h.hierarchy) !== null && _j !== void 0 ? _j : latestValidateResponse === null || latestValidateResponse === void 0 ? void 0 : latestValidateResponse.hierarchy) !== null && _k !== void 0 ? _k : [];
    hierarchyList.forEach(function (hierarchy) {
        if (hierarchy.levels.length === 0)
            return;
        var levels = [];
        hierarchy.levels.forEach(function (level) {
            var _a, _b;
            levels.push((_b = (_a = level === null || level === void 0 ? void 0 : level[languageCode]) === null || _a === void 0 ? void 0 : _a.desc) !== null && _b !== void 0 ? _b : '');
        });
        catalogGroups.push(levels);
    });
    var handleActionInParent = function (event) {
        if (event === 'kitParent') {
            setIsModalOpen(function (prevVal) { return !prevVal; });
            setModalParam('kitParent');
        }
    };
    var colDef = {
        rowGap: '4px',
        title: { displayText: 'Product Details', marginBottom: '16px' },
        showActions: false,
        preventReadOnly: true,
        fields: (_b = {},
            _b[PRODUCT_PREFIX] = {
                fieldName: 'Prefix',
                displaySeq: 0,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[CHANNELS] = {
                fieldName: 'Channels',
                displaySeq: 1,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[MIN_LEAD_TIME] = {
                fieldName: 'Defined Minimum Lead Time',
                displaySeq: 2,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[KIT_PARENT] = {
                fieldName: 'Kit',
                displaySeq: 3,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[ORDERABLEIND] = {
                fieldName: 'Orderable',
                displaySeq: 4,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[PRICE_MATRIX_ID] = {
                fieldName: 'Price Matrix ID',
                displaySeq: 5,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[PRODUCT_VERSION] = {
                fieldName: 'Product Version',
                displaySeq: 6,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[PRODUCT_START_DATE] = {
                fieldName: 'Product Start Date',
                displaySeq: 7,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[PRODUCT_END_DATE] = {
                fieldName: 'Product End Date',
                displaySeq: 8,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b[BASE_PRODUCT_DESCRIPTION] = {
                fieldName: 'Description:',
                displaySeq: 9,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _b),
    };
    var colDefCode = {
        rowGap: '4px',
        title: { displayText: 'Product Codes', marginBottom: '16px' },
        showActions: false,
        preventReadOnly: true,
        fields: (_c = {},
            _c[BUSINESS_UNIT_CODE] = {
                fieldName: 'Business Unit (Code)',
                displaySeq: 0,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _c[BRAND_CODE] = {
                fieldName: 'Brand (Code)',
                displaySeq: 1,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _c[PRODUCT_CLASS] = {
                fieldName: 'Product Class',
                displaySeq: 2,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _c[DISCOUNT_CODE] = {
                fieldName: 'Discount (Code)',
                displaySeq: 3,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _c[PRODUCT_LINE_CODE] = {
                fieldName: 'Product Line (Code)',
                displaySeq: 4,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _c[PRODUCT_FUNCTION_CODE] = {
                fieldName: 'Product Function (Code)',
                displaySeq: 5,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _c[WEIGHT_CLASS_CODE] = {
                fieldName: 'Weight Class (Code)',
                displaySeq: 6,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _c),
    };
    var colDefCustom = {
        rowGap: '4px',
        title: { displayText: 'Custom Details', marginBottom: '16px' },
        showActions: false,
        preventReadOnly: true,
        fields: (_d = {},
            _d[STANDARD_PRODUCT_REFERENCE] = {
                fieldName: 'Standard Product Reference',
                displaySeq: 0,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[COMPLEXITY] = {
                fieldName: 'Manufacturing Complexity',
                displaySeq: 1,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[PROPRIETARY_IND] = {
                fieldName: 'Proprietary Ind',
                displaySeq: 2,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[FSC_TYPE] = {
                fieldName: 'FSC Types',
                displaySeq: 3,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[FSC_CHAIN_OF_CUSTODY] = {
                fieldName: 'FSC Chain of Custody',
                displaySeq: 4,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[REQUESTOR] = {
                fieldName: 'Requestor',
                displaySeq: 5,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[QUOTE_STATUS] = {
                fieldName: 'Quote Status',
                displaySeq: 6,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[ENGINEERING_STATUS] = {
                fieldName: 'Engineering Status',
                displaySeq: 7,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[ENGINEERING_SITE] = {
                fieldName: 'Engineering Site',
                displaySeq: 8,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d[DEVIATION_DESCRIPTION] = {
                fieldName: 'Deviation Description',
                displaySeq: 9,
                hasLookup: false,
                input: {
                    type: InputFieldType.READONLY,
                },
            },
            _d),
    };
    var handleModalClose = function () {
        setJsonViewerOpen(false);
    };
    return (_jsxs(Box, { children: [!isLoading ? (_jsxs(Box, __assign({ className: styles.headerContainer }, { children: [_jsx(Box, __assign({ className: styles.dataIcon }, { children: _jsx(Typography, __assign({ sx: { textDecoration: 'underline', fontSize: '13px' }, onMouseUp: function () { return setJsonViewerOpen(true); } }, { children: "View JSON Response" })) })), _jsx(JsonViewer, { isOpen: isJsonViewerOpen, closeModal: function () { return handleModalClose(); }, stringifiedJson: JSON.stringify(latestValidateResponse, null, 4) }), _jsx(Box, __assign({ className: styles.headerLeftContainer, sx: { marginRight: '30px' } }, { children: _jsx(InputFieldsContainer, { colDef: colDef, fieldValues: fieldValues, labelFieldNameWidth: "190px", lineHeight: "20px", isCompleteProduct: isCompleteProduct, fireActionInParent: function (e) { return handleActionInParent(e); } }) })), _jsxs(Box, __assign({ className: styles.headerRightHalfContainer }, { children: [_jsx(InputFieldsContainer, { colDef: colDefCode, fieldValues: fieldValuesCode, labelFieldNameWidth: "170px", lineHeight: "20px" }), _jsxs(Box, __assign({ className: styles.catalogContainer }, { children: [_jsx(Typography, __assign({ variant: "h2", className: styles.catalogTitle }, { children: "Catalog Hierarchies" })), catalogGroups.map(function (levelGroup, index) { return (_jsxs(Box, __assign({ className: styles.firstCatalog }, { children: [levelGroup.map(function (levelText, i) { return (_jsxs(Box, __assign({ display: "flex", flexDirection: "row" }, { children: [_jsx(Typography, __assign({ className: index === 0 ? styles.value : '', sx: { fontSize: '13px !important' } }, { children: levelText })), i !== levelGroup.length - 1 && (_jsx(Typography, __assign({ className: styles.arrow }, { children: '>' })))] }), Math.random())); }), index === 0 && (_jsx(Typography, __assign({ className: styles.value, sx: { paddingLeft: 0.5 } }, { children: "(P)" })))] }), Math.random())); })] }))] })), _jsx(Box, __assign({ className: styles.buildBoxContainer }, { children: _jsx(BuildBox, { latestValidateResponse: latestValidateResponse, timestamp: timestamp, channelVal: channelVal, buildMode: buildMode, routeItems: routeItems }) }))] }))) : (_jsx(LoadingSpinner, { indicatorSize: 10 })), showCustom ? (_jsx(Box, __assign({ className: styles.dataContainer }, { children: _jsx(InputFieldsContainer, { colDef: colDefCustom, fieldValues: feildValuesCustom, labelFieldNameWidth: "200px", lineHeight: "40px" }) }))) : null, isModalOpen && modalParam === 'kitParent' && (_jsx(Box, __assign({ sx: { position: 'absolute' } }, { children: _jsx(StyledDialogV2, __assign({ isOpen: isModalOpen, setIsOpen: setIsModalOpen, sizeProps: { width: '90%', maxWidth: '1800px' }, title: (listOfKitParents && (listOfKitParents === null || listOfKitParents === void 0 ? void 0 : listOfKitParents.length) > 0)
                        ? 'Parent Kits'
                        : 'Child Kits' }, { children: _jsx(ChildKitsModal, { isChild: (listOfKitParents && (listOfKitParents === null || listOfKitParents === void 0 ? void 0 : listOfKitParents.length) > 0), kitData: listOfKitParents && (listOfKitParents === null || listOfKitParents === void 0 ? void 0 : listOfKitParents.length) > 0
                            ? listOfKitParents
                            : latestValidateResponse.kitChildren, metadata: {
                            pn: latestValidateResponse.productNbrOut,
                            desc: latestValidateResponse.baseProductDescription,
                        } }) })) })))] }));
}
var buildFieldValuesCustom = function (infoResponse) {
    var _a;
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1;
    return (_a = {},
        _a[STANDARD_PRODUCT_REFERENCE] = {
            value: (_c = (_b = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _b === void 0 ? void 0 : _b.header) === null || _c === void 0 ? void 0 : _c.standardProductReference,
        },
        _a[COMPLEXITY] = {
            value: (_e = (_d = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _d === void 0 ? void 0 : _d.header) === null || _e === void 0 ? void 0 : _e.complexity,
        },
        _a[PROPRIETARY_IND] = {
            value: (_h = (_g = (_f = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _f === void 0 ? void 0 : _f.header) === null || _g === void 0 ? void 0 : _g.custom) === null || _h === void 0 ? void 0 : _h.customProprietaryInd,
        },
        _a[FSC_TYPE] = {
            value: (_k = (_j = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _j === void 0 ? void 0 : _j.header) === null || _k === void 0 ? void 0 : _k.fscType,
        },
        _a[FSC_CHAIN_OF_CUSTODY] = {
            value: (_m = (_l = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _l === void 0 ? void 0 : _l.header) === null || _m === void 0 ? void 0 : _m.fscChainOfCustody,
        },
        _a[REQUESTOR] = {
            value: (_q = (_p = (_o = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _o === void 0 ? void 0 : _o.header) === null || _p === void 0 ? void 0 : _p.custom) === null || _q === void 0 ? void 0 : _q.customRequestor,
        },
        _a[QUOTE_STATUS] = {
            value: (_t = (_s = (_r = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _r === void 0 ? void 0 : _r.header) === null || _s === void 0 ? void 0 : _s.custom) === null || _t === void 0 ? void 0 : _t.customQuoteStatus,
        },
        _a[ENGINEERING_STATUS] = {
            value: (_w = (_v = (_u = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _u === void 0 ? void 0 : _u.header) === null || _v === void 0 ? void 0 : _v.custom) === null || _w === void 0 ? void 0 : _w.customEngStatus,
        },
        _a[ENGINEERING_SITE] = {
            value: (_z = (_y = (_x = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _x === void 0 ? void 0 : _x.header) === null || _y === void 0 ? void 0 : _y.custom) === null || _z === void 0 ? void 0 : _z.customEngSite,
        },
        _a[DEVIATION_DESCRIPTION] = {
            value: (_1 = (_0 = infoResponse === null || infoResponse === void 0 ? void 0 : infoResponse.details) === null || _0 === void 0 ? void 0 : _0.header) === null || _1 === void 0 ? void 0 : _1.deviationDesc,
        },
        _a);
};
var buildFieldValuesCodes = function (validateDetail) {
    var _a;
    return (_a = {},
        _a[BUSINESS_UNIT_CODE] = {
            value: validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.businessUnitCode,
        },
        _a[BRAND_CODE] = {
            value: validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.brandCode,
        },
        _a[PRODUCT_CLASS] = {
            value: validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productClass,
        },
        _a[DISCOUNT_CODE] = {
            value: "".concat(validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.discountDesc, " (").concat(validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.discountCode, ")"),
        },
        _a[PRODUCT_LINE_CODE] = {
            value: "".concat(validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productLineDesc, " (").concat(validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productLineCode, ")"),
        },
        _a[PRODUCT_FUNCTION_CODE] = {
            value: "".concat(validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productFunctionDesc, " (").concat(validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productFunctionCode, ")"),
        },
        _a[WEIGHT_CLASS_CODE] = {
            value: validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.weightClassCode,
        },
        _a);
};
var buildFieldValues = function (validateDetail, infoDetail, channelValue, listOfKitParents) {
    var _a;
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    return (_a = {},
        _a[PRODUCT_PREFIX] = {
            value: validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productPrefix,
        },
        _a[CHANNELS] = {
            value: channelValue,
        },
        _a[MIN_LEAD_TIME] = {
            value: (validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.minLeadtime) !== 99 ? "".concat(validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.minLeadtime, " Days") : 'Assigned',
        },
        _a[KIT_PARENT] = {
            /* eslint-disable no-nested-ternary */
            value: (validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.kitParent) ? 'Parent' : listOfKitParents && listOfKitParents.length > 0 ? 'Child' : '',
            link: true,
            linkText: listOfKitParents && listOfKitParents.length > 0 ? 'View Parents' : 'View Children',
        },
        _a[ORDERABLEIND] = {
            value: (validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.orderableInd) ? 'Yes' : 'No',
        },
        _a[PRICE_MATRIX_ID] = {
            value: (_b = validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.priceMatrixId) !== null && _b !== void 0 ? _b : (_d = (_c = infoDetail === null || infoDetail === void 0 ? void 0 : infoDetail.details) === null || _c === void 0 ? void 0 : _c.header) === null || _d === void 0 ? void 0 : _d.priceMatrixId,
        },
        _a[PRODUCT_VERSION] = {
            value: (_e = validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productVersion) !== null && _e !== void 0 ? _e : (_g = (_f = infoDetail === null || infoDetail === void 0 ? void 0 : infoDetail.details) === null || _f === void 0 ? void 0 : _f.header) === null || _g === void 0 ? void 0 : _g.productVersion,
        },
        _a[PRODUCT_START_DATE] = {
            value: DateTime.fromISO((_k = (_j = (_h = infoDetail === null || infoDetail === void 0 ? void 0 : infoDetail.details) === null || _h === void 0 ? void 0 : _h.header) === null || _j === void 0 ? void 0 : _j.startDate) !== null && _k !== void 0 ? _k : '').toFormat('MM/dd/yyyy'),
        },
        _a[PRODUCT_END_DATE] = {
            value: DateTime.fromISO((_o = (_m = (_l = infoDetail === null || infoDetail === void 0 ? void 0 : infoDetail.details) === null || _l === void 0 ? void 0 : _l.header) === null || _m === void 0 ? void 0 : _m.endDate) !== null && _o !== void 0 ? _o : '').toFormat('MM/dd/yyyy'),
        },
        _a[BASE_PRODUCT_DESCRIPTION] = {
            value: validateDetail === null || validateDetail === void 0 ? void 0 : validateDetail.productName,
        },
        _a);
};
